import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Navbar from '../../components/navBar';
import WheelPagePreview from '../../components/wheelPagePreview/WheelPagePreview';
import { fetchBackgroundUrl, uploadBackground } from '../../api';
import '../AdminPage/Admin.css';
import './BackgroundSettings.css';

const defaultBackground = require('../../assets/backgroundneedReplace.jpg');

const BackgroundSettingsPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [currentBackgroundUrl, setCurrentBackgroundUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadCurrentBackground = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = await fetchBackgroundUrl();
      setCurrentBackgroundUrl(url || defaultBackground);
    } catch (error) {
      console.error('Lỗi tải hình nền:', error);
      setCurrentBackgroundUrl(defaultBackground);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isAuthenticated');
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    loadCurrentBackground();
  }, [navigate, loadCurrentBackground]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Chỉ chấp nhận file JPG, PNG hoặc WEBP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File quá lớn. Tối đa 5MB.');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!selectedFile) {
      toast.error('Vui lòng chọn hình ảnh trước khi lưu');
      return;
    }

    setIsSaving(true);
    try {
      const url = await uploadBackground(selectedFile);
      setCurrentBackgroundUrl(url || previewUrl);
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast.success('Cập nhật hình nền thành công!');
    } catch (error) {
      console.error('Lỗi upload hình nền:', error);
      toast.error(error.response?.data?.message || 'Upload thất bại');
    } finally {
      setIsSaving(false);
    }
  };

  const renderPreviewSection = (backgroundUrl, title, emptyText) => (
    <div className="bg-settings-preview-block">
      <h3 className="bg-settings-section-title">{title}</h3>
      {backgroundUrl ? (
        <WheelPagePreview backgroundUrl={backgroundUrl} />
      ) : (
        <p className="bg-settings-empty">{emptyText}</p>
      )}
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="admin-container bg-settings-container">
        <h2 className="admin-title">Quản lý hình nền</h2>
        <p className="bg-settings-note">
          Chọn hình nền từ máy tính, xem trước đầy đủ giao diện (vòng quay, nút Quay Ngay, footer) rồi lưu.
          Khuyến nghị kích thước banner ngang ~1280x632px. Tối đa 5MB.
        </p>

        <div className="bg-settings-actions">
          <input
            ref={fileInputRef}
            id="background-file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="bg-settings-file-input"
            onChange={handleFileChange}
          />
          <label htmlFor="background-file" className="bg-settings-file-label">
            Chọn hình từ máy tính
          </label>
          <button
            type="button"
            className="btn-submit"
            onClick={handleSave}
            disabled={!selectedFile || isSaving}
          >
            {isSaving ? 'Đang lưu...' : 'Lưu hình nền'}
          </button>
        </div>

        <div className="bg-settings-grid">
          {renderPreviewSection(
            isLoading ? null : currentBackgroundUrl,
            'Giao diện hiện tại',
            'Đang tải...'
          )}
          {renderPreviewSection(
            previewUrl,
            'Xem trước sau khi đổi hình',
            'Chưa chọn hình mới'
          )}
        </div>

        <ToastContainer position="top-right" autoClose={5000} />
      </div>
    </>
  );
};

export default BackgroundSettingsPage;
